import React, { useState, useRef } from "react";
import "./WebinarForm.css";

const WebinarForm = () => {
  const initialFormData = {
    firstName: "",
    lastName: "",
    profession: "",
    course: "",
    phone: "",
    email: "",
    message: ""
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Refs for focusing on error fields
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const professionRef = useRef();
  const courseRef = useRef();
  const phoneRef = useRef();
  const emailRef = useRef();

  // Validate fields
  const validate = () => {
    let tempErrors = {};

    if (!formData.firstName) tempErrors.firstName = "First Name is required";
    if (!formData.lastName) tempErrors.lastName = "Last Name is required";
    if (!formData.profession) tempErrors.profession = "Profession is required";
    if (!formData.course) tempErrors.course = "Course is required";

    // Phone number: 10 digits only
    if (!formData.phone) {
      tempErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      tempErrors.phone = "Enter a valid 10-digit phone number";
    }

    // Email: if provided, must be valid format
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      tempErrors.email = "Enter a valid email address";
    }

    setErrors(tempErrors);
    return tempErrors;
  };

  // Focus first error field
  const focusFirstError = (errors) => {
    if (errors.firstName) return firstNameRef.current.focus();
    if (errors.lastName) return lastNameRef.current.focus();
    if (errors.profession) return professionRef.current.focus();
    if (errors.course) return courseRef.current.focus();
    if (errors.phone) return phoneRef.current.focus();
    if (errors.email) return emailRef.current.focus();
  };

  // Update form fields
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" }); // remove error on typing
  };

  // Reset form fields
  const handleReset = () => {
    setFormData(initialFormData);
    setErrors({});
    setSubmitted(false);
  };

  // Submit form to PHP backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      focusFirstError(validationErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("https://express-brown-omega.vercel.app/api/enrollmentmail", {
        method: "POST",
        body: JSON.stringify({
          "name": `${formData.firstName} ${formData.lastName}`,
          "profession": formData.profession,
          "phone": formData.phone,
          "email": formData.email,
          "message": formData.message,
          "batch": formData.batch,
          "course": formData.course,
          "termsAccepted": true
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result.success === true) {
        setSubmitted(true);
        alert("Thank you for enrolling to the Webinar! Please mark the calendar for the webinar.");
        handleReset();
      } else {
        alert("Failed to send email. Please try again.");
        console.error(result.message);
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong. Please try again.");
    }

    setLoading(false);
  };

  return (
    <div className="webinar-container">
      <h2 style={{ textAlign: "center" }}>Our next free webinar is on <br/> 19th October, Sunday - 11:00 AM</h2>
      {!submitted ? (
        <form className="webinar-form" onSubmit={handleSubmit}>
          <input
            type="text"
            name="firstName"
            placeholder="First Name *"
            value={formData.firstName}
            onChange={handleChange}
            ref={firstNameRef}
          />
          {errors.firstName && <p className="error-msg">{errors.firstName}</p>}

          <input
            type="text"
            name="lastName"
            placeholder="Last Name *"
            value={formData.lastName}
            onChange={handleChange}
            ref={lastNameRef}
          />
          {errors.lastName && <p className="error-msg">{errors.lastName}</p>}

          <input
            type="text"
            name="profession"
            placeholder="Profession *"
            value={formData.profession}
            onChange={handleChange}
            ref={professionRef}
          />
          {errors.profession && <p className="error-msg">{errors.profession}</p>}

          <select
            name="course"
            value={formData.course}
            onChange={handleChange}
            ref={courseRef}
          >
            <option value="">Select Course *</option>
            <option value="Beginner">Beginner</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Advanced">Advanced</option>
            <option value="TOEFL & IELTS">TOEFL & IELTS</option>
          </select>
          {errors.course && <p className="error-msg">{errors.course}</p>}

          <input
            type="text"
            name="phone"
            placeholder="Phone Number *"
            value={formData.phone}
            onChange={handleChange}
            ref={phoneRef}
          />
          {errors.phone && <p className="error-msg">{errors.phone}</p>}

          <input
            type="email"
            name="email"
            placeholder="Email*"
            value={formData.email}
            onChange={handleChange}
            ref={emailRef}
            required
          />
          {errors.email && <p className="error-msg">{errors.email}</p>}

          <textarea
            name="message"
            placeholder="Any message"
            value={formData.message}
            onChange={handleChange}
          />

          <div className="btn-group">
            <button type="button" className="submit-btn" onClick={handleReset}>
              Reset
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      ) : (
        <p className="success-msg">
          Thank you for enrolling to the Webinar! Please mark the calendar for the webinar.
        </p>
      )}
    </div>
  );
};

export default WebinarForm;
